import type { ResourceRunStatus, RunEvent, RunProgress, RunStatus } from '../api/types';

export interface EventLogState {
  /** Events in ascending sequence order, without duplicates. */
  events: RunEvent[];
  /** Highest sequence received (used to resume the stream). */
  lastSequence: number;
  resourceStatus: Record<string, ResourceRunStatus>;
  progress: RunProgress | null;
  runStatus: RunStatus | null;
}

export type EventLogAction = { type: 'received'; events: readonly RunEvent[] } | { type: 'reset' };

export const initialEventLog: EventLogState = {
  events: [],
  lastSequence: 0,
  resourceStatus: {},
  progress: null,
  runStatus: null,
};

function apply(state: EventLogState, event: RunEvent): void {
  const data = event.data ?? {};
  switch (event.kind) {
    case 'resource-status':
      if (event.resourceId && typeof data.status === 'string') {
        state.resourceStatus[event.resourceId] = data.status as ResourceRunStatus;
      }
      break;
    case 'progress':
      if (typeof data.completed === 'number' && typeof data.total === 'number') {
        state.progress = { completed: data.completed, total: data.total };
      }
      break;
    case 'run-status':
      if (typeof data.status === 'string') state.runStatus = data.status as RunStatus;
      break;
    default:
      break;
  }
}

function derive(events: RunEvent[]): EventLogState {
  const state: EventLogState = {
    events,
    lastSequence: events.length ? events[events.length - 1].sequence : 0,
    resourceStatus: {},
    progress: null,
    runStatus: null,
  };
  for (const event of events) apply(state, event);
  return state;
}

/**
 * Merges events by sequence. Duplicates (for example after a reconnect replays events) are
 * dropped, and events that arrive out of order are put in place; derived state always reflects
 * the events in sequence order.
 */
export function eventLogReducer(state: EventLogState, action: EventLogAction): EventLogState {
  if (action.type === 'reset') return initialEventLog;

  const known = new Set(state.events.map((e) => e.sequence));
  const fresh: RunEvent[] = [];
  for (const event of action.events) {
    if (known.has(event.sequence)) continue;
    known.add(event.sequence);
    fresh.push(event);
  }
  if (fresh.length === 0) return state;
  fresh.sort((a, b) => a.sequence - b.sequence);

  if (fresh[0].sequence > state.lastSequence) {
    // Common case: everything is newer; apply incrementally.
    const next: EventLogState = {
      ...state,
      events: [...state.events, ...fresh],
      resourceStatus: { ...state.resourceStatus },
      lastSequence: fresh[fresh.length - 1].sequence,
    };
    for (const event of fresh) apply(next, event);
    return next;
  }

  return derive([...state.events, ...fresh].sort((a, b) => a.sequence - b.sequence));
}
